import { useState, FormEvent } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { supabase } from '../lib/supabaseClient';
import Header from '../components/Header';
import Button from '../components/Button';
import SuccessModal from '../components/SuccessModal';

const CATEGORIES = [
  'Bug or Technical Issue',
  'Something Is Confusing',
  'Feature or Idea',
  'Content or Storytelling',
  'General Feedback',
];

const APP_VERSION = '1.0.0';

export default function Feedback() {
  const { navigateTo, navigateBack } = useApp();
  const { user } = useEffectiveUser();

  const [category, setCategory] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  const validateEmail = (emailValue: string): boolean => {
    if (!emailValue.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value.trim() && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setValidationError('');

    // Honeypot spam protection - if filled, reject silently
    if (honeypot) {
      console.log('Honeypot triggered - potential spam');
      setIsSubmitting(false);
      return;
    }

    if (!message.trim() || message.trim().length < 10) {
      setValidationError('Please add a short message so we know how to improve.');
      return;
    }

    if (email.trim() && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const deviceInfo = {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      };

      const { error: insertError } = await supabase
        .from('feedback_submissions')
        .insert([
          {
            user_id: user?.id || null,
            email: email.trim() || null,
            category: category || null,
            message: message.trim(),
            app_version: APP_VERSION,
            device_info: deviceInfo,
          },
        ]);

      if (insertError) {
        console.error('[Feedback] Error inserting feedback:', insertError);
        throw new Error('Failed to save feedback');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-new-feedback`;
      const headers = {
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const payload = {
        message: message.trim(),
        email: email.trim() || undefined,
        category: category || undefined,
        appVersion: APP_VERSION,
        deviceInfo,
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('[Feedback] Error calling edge function:', await response.text());
        throw new Error('Failed to send notification');
      }

      setCategory('');
      setMessage('');
      setEmail('');
      setShowSuccess(true);
    } catch (err) {
      console.error('[Feedback] Submission error:', err);
      setError('Something went wrong while sending your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigateTo('home');
  };

  return (
    <>
      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        title="Thank you for helping shape civicbeat"
        body="Your feedback has been sent. We'll use it to improve the way we surface songs, stories, and civic meaning."
        noteBox="We may not be able to reply to every message, but every one is read by a human."
      />

      <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <Header
          mode="title"
          title=""
          showBackButton
          backButtonLabel="Back"
          onBackClick={navigateBack}
        />

        <div className="w-full max-w-3xl mx-auto px-4 py-6 lg:py-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ letterSpacing: '-0.005em' }}>
          Share Feedback
        </h1>

        <p className="text-[15px] text-gray-300 mb-8" style={{ lineHeight: '1.55' }}>
          Tell us what works, what's confusing, or what you'd love to see next.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              What's this about? <span className="text-gray-500 text-sm font-normal">(optional)</span>
            </label>
            <p className="text-sm text-gray-400 mb-4" style={{ lineHeight: '1.5' }}>
              A quick label helps us route your feedback.
            </p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat === category ? '' : cat)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    category === cat
                      ? 'bg-[#40E0D0]/15 text-white border-[#40E0D0]/40 border'
                      : 'bg-[#111111] text-gray-300 border hover:bg-[#1A1A1A] active:bg-[#0F0F0F]'
                  }`}
                  style={category !== cat ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              What's on your mind? <span className="text-[#FF6F61]">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setValidationError('');
              }}
              placeholder="Share any thoughts, ideas, or issues. The more specific you are, the more useful it is for us."
              rows={5}
              className={`w-full px-4 py-3 bg-[#111111] border ${
                validationError ? 'border-[#FF6F61]' : ''
              } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all resize-none`}
              style={!validationError ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
            />
            {validationError && (
              <p className="text-[#FF6F61] text-sm mt-1">{validationError}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              We read every message, even if we can't reply to all of them.
            </p>
          </div>

          <div>
            <label htmlFor="email" className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              Email <span className="text-gray-500 text-sm font-normal">(optional)</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="you@example.com"
              className={`w-full px-4 py-3 bg-[#111111] border ${
                emailError ? 'border-[#FF6F61]' : ''
              } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all`}
              style={!emailError ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
            />
            {emailError && (
              <p className="text-[#FF6F61] text-sm mt-1">{emailError}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Share your email if you're open to us following up. We'll only use it for this conversation.
            </p>
          </div>

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Honeypot field - hidden from users, catches bots */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <div className="pt-4">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting || !!emailError}
            >
              {isSubmitting ? 'Sending…' : 'Send Feedback'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
}
